import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';

// ---------------------------------------------------------------------------
// CircularProgress – SVG ring progress indicator
// ---------------------------------------------------------------------------

/**
 * Color theme options for the progress ring
 */
export type CircularProgressColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'red'
  | 'green'
  | 'indigo'
  | 'gray';

/**
 * Size presets for the circular progress
 */
export type CircularProgressSize = 'sm' | 'md' | 'lg' | 'xl';

interface CircularProgressSignature {
  Args: {
    /** Current value (0 to max) */
    value: number;
    /** Maximum value (default: 100) */
    max?: number;
    /** Color theme for the progress ring */
    color?: CircularProgressColor;
    /** Size preset (default: 'md') */
    size?: CircularProgressSize;
    /** Stroke width (default: auto based on size) */
    strokeWidth?: number;
    /** Whether to animate the ring on mount */
    animated?: boolean;
    /** Custom aria-label */
    label?: string;
  };
  Blocks: {
    /** Content rendered inside the ring (e.g., value text, icon) */
    default?: [];
  };
  Element: HTMLDivElement;
}

const colorStrokeClasses: Record<CircularProgressColor, string> = {
  emerald: 'stroke-emerald-500',
  blue: 'stroke-blue-500',
  violet: 'stroke-violet-500',
  amber: 'stroke-amber-500',
  teal: 'stroke-teal-500',
  red: 'stroke-red-500',
  green: 'stroke-green-500',
  indigo: 'stroke-indigo-500',
  gray: 'stroke-gray-500',
};

const sizeDimensions: Record<CircularProgressSize, { size: number; stroke: number; text: string }> =
  {
    sm: { size: 48, stroke: 4, text: 'text-sm' },
    md: { size: 80, stroke: 6, text: 'text-lg' },
    lg: { size: 120, stroke: 8, text: 'text-2xl' },
    xl: { size: 160, stroke: 10, text: 'text-3xl' },
  };

/**
 * CircularProgress - An SVG ring progress indicator
 *
 * Displays a circular ring that fills based on the value/max ratio.
 * Supports named block content inside the ring for value display.
 *
 * @example Basic usage
 * ```gts
 * import CircularProgress from 'tio-reimagined/components/circular-progress';
 *
 * <CircularProgress @value={{75}} @color="emerald">
 *   75%
 * </CircularProgress>
 * ```
 *
 * @example Large with custom content
 * ```gts
 * <CircularProgress @value={{720}} @max={{1000}} @color="violet" @size="lg">
 *   <div class="text-center">
 *     <div class="text-2xl font-bold">720</div>
 *     <div class="text-xs text-gray-500">of 1,000</div>
 *   </div>
 * </CircularProgress>
 * ```
 */
export default class CircularProgress extends Component<CircularProgressSignature> {
  get max() {
    const max = this.args.max ?? 100;
    return max > 0 ? max : 100;
  }

  get percent() {
    const value = Math.max(0, Math.min(this.args.value, this.max));
    return Math.round((value / this.max) * 100);
  }

  get sizeKey() {
    return this.args.size ?? 'md';
  }

  get dimensions() {
    return sizeDimensions[this.sizeKey];
  }

  get svgSize() {
    return this.dimensions.size;
  }

  get center() {
    return this.svgSize / 2;
  }

  get strokeWidth() {
    return this.args.strokeWidth ?? this.dimensions.stroke;
  }

  get radius() {
    return (this.svgSize - this.strokeWidth) / 2;
  }

  get circumference() {
    return 2 * Math.PI * this.radius;
  }

  get dashOffset() {
    return this.circumference * (1 - this.percent / 100);
  }

  get containerStyle() {
    return htmlSafe(`width: ${this.svgSize}px; height: ${this.svgSize}px`);
  }

  get strokeClass() {
    return colorStrokeClasses[this.args.color ?? 'blue'];
  }

  get ariaLabel() {
    return this.args.label ?? `${this.percent}% complete`;
  }

  <template>
    <div
      data-test-circular-progress
      class="relative inline-flex items-center justify-center"
      style={{this.containerStyle}}
      role="progressbar"
      aria-valuenow={{this.percent}}
      aria-valuemin={{0}}
      aria-valuemax={{100}}
      aria-label={{this.ariaLabel}}
      ...attributes
    >
      <svg
        role="presentation"
        class="{{if @animated 'transition-all duration-700'}} -rotate-90"
        width={{this.svgSize}}
        height={{this.svgSize}}
      >
        {{! Background track }}
        <circle
          role="presentation"
          class="stroke-gray-200 dark:stroke-gray-700"
          fill="none"
          stroke-width={{this.strokeWidth}}
          cx={{this.center}}
          cy={{this.center}}
          r={{this.radius}}
        />
        {{! Progress ring }}
        <circle
          role="presentation"
          data-test-circular-progress-ring
          class="{{this.strokeClass}} {{if @animated 'transition-all duration-700'}}"
          fill="none"
          stroke-width={{this.strokeWidth}}
          stroke-linecap="round"
          cx={{this.center}}
          cy={{this.center}}
          r={{this.radius}}
          stroke-dasharray={{this.circumference}}
          stroke-dashoffset={{this.dashOffset}}
        />
      </svg>
      {{#if (has-block)}}
        <div
          data-test-circular-progress-content
          class="absolute inset-0 flex items-center justify-center"
        >
          {{yield}}
        </div>
      {{/if}}
    </div>
  </template>
}

export type { CircularProgressSignature };
